import {WeeklyObservationsV2} from '@shared/api/models/query/patient-weekly-snapshot.model';

/**
 * Portal-side ARFID vs standard (Anorexia/RHR) copy for the carer weekly summary page
 * (UNIVAPLAT-3033). Ported verbatim from the app's carer weekly check-in copy — see
 * `app.univa.health` `lib/features/shared/weekly_check_in/data/carer_copy_variants.dart`
 * (observation labels, meal plan framing) and
 * `.../presentation/carer/carer_how_its_going_step.dart` (How it's going answer labels).
 * Structure and area ids are identical across variants; only display copy differs — the
 * same "gate on careProgramScope" contract the app uses (see the snapshot's top-level
 * `careProgramScope`, UNIVAPLAT-3053).
 */

export type WeeklyObservationLabels = Record<keyof WeeklyObservationsV2, string>;

export interface WeeklyHowItsGoingAnswerLabels {
    progressFeeling: { notGood: string; okay: string; pleased: string };
    univaHelpfulness: { notVery: string; aLittle: string; very: string };
}

export interface WeeklyCarerCopy {
    observationLabels: WeeklyObservationLabels;
    mealPlanTitle: string;
    howItsGoingAnswerLabels: WeeklyHowItsGoingAnswerLabels;
}

// Identical between variants — carer_how_its_going_step.dart does not branch on ARFID.
const HOW_ITS_GOING_ANSWER_LABELS: WeeklyHowItsGoingAnswerLabels = {
    progressFeeling: {notGood: 'Not good', okay: 'Okay', pleased: 'Pleased'},
    univaHelpfulness: {notVery: 'Not very', aLittle: 'A little', very: 'Very'},
};

const STANDARD_CARER_COPY: WeeklyCarerCopy = {
    observationLabels: {
        eatingAtMealtimes: 'Eating at mealtimes',
        finishingPortions: 'Finishing portions',
        varietyOfFoods: 'Variety of foods',
        anxietyAroundFood: 'Anxiety around food',
        overallProgress: "How it's going",
    },
    mealPlanTitle: 'Meal plan',
    howItsGoingAnswerLabels: HOW_ITS_GOING_ANSWER_LABELS,
};

const ARFID_CARER_COPY: WeeklyCarerCopy = {
    observationLabels: {
        eatingAtMealtimes: 'Eating at mealtimes',
        finishingPortions: 'Interest in eating',
        varietyOfFoods: 'Variety of foods',
        anxietyAroundFood: 'Anxiety around food',
        overallProgress: 'Mealtime atmosphere',
    },
    mealPlanTitle: "This week's eating",
    howItsGoingAnswerLabels: HOW_ITS_GOING_ANSWER_LABELS,
};

/**
 * Resolves the carer weekly summary copy for the patient's care programme.
 * `isArfid` should be derived from the snapshot's top-level `careProgramScope`
 * (UNIVAPLAT-3053): `careProgramScope === 'ARFID'`. `null` or any other value falls back
 * to the standard (Anorexia/RHR) copy — see UNIVAPLAT-2962 OQ-FE2.
 */
export function resolveWeeklyCarerCopy(isArfid: boolean): WeeklyCarerCopy {
    return isArfid ? ARFID_CARER_COPY : STANDARD_CARER_COPY;
}
